import moment from 'moment';
import MoblicoTokenService from 'src/services/MoblicoTokenService';
import MoblicoApi from 'src/api/MoblicoApi';
import DealRepository from './aws/DealRepository';
import type {MoblicoPromotionType} from 'src/types/Promotions';
import Events, {PromotionActions} from 'src/logging/Events';
import Deal from 'src/models/Moblico/Deal';
import type {Deal as ServerDeal} from 'src/types/serverTypes/Moblico';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import {alertError} from 'src/components/helpers/AlertHelper';

import Localized from 'src/constants/AppStrings';
import AccountStore from 'src/stores/AccountStore';

class DealService {
  async loadMoblicoDeals(email: string) {
    try {
      const token = await MoblicoTokenService.getToken(email);
      const response: Array<ServerDeal> = await MoblicoApi.getDeals(token);

      if (response) {
        this.reconcileDeals(response);
      } else {
        this.reconcileDeals([]);
      }
    } catch (error) {
      // Only log the error if it's something other than a 404 from the request.
      if (
        error &&
        error.networkResponse &&
        error.networkResponse.status !== 404
      ) {
        CrashlyticsEvents.log(
          'Exception',
          'DealService:loadMoblicoDeals',
          generateErrorMessage(error),
        );
        Events.Error.trackEvent(
          'Exception',
          'DealService:loadMoblicoDeals',
          generateErrorMessage(error),
        );
      }
    }
  }

  private reconcileDeals(remoteDeals: Array<ServerDeal>) {
    const deals = remoteDeals.map((deal: ServerDeal) => {
      return new Deal(deal);
    });
    DealRepository.addDeals(deals);
  }

  async acceptMoblicoDeal(email: string, id: string) {
    const token = await MoblicoTokenService.getToken(email);
    const response = await MoblicoApi.acceptDeal(token, id);
    if (response?.statusCode === -1) {
      alertError(Localized.Errors.failed_fetch_deal_token);
    } else if (!response || response?.statusCode === 200) {
      const deal = DealRepository.getDeal(id);
      Events.Promotion.trackEvent(
        id.toString(),
        deal.name,
        PromotionActions.ClaimedDeal,
      );
      DealRepository.acceptDeal(id);
    }
  }

  getDeals() {
    return DealRepository.getDeals();
  }

  matchPromosWithDeals(
    promotions: Array<MoblicoPromotionType>,
  ): Array<MoblicoPromotionType> {
    if (promotions) {
      return promotions.map((promotion) => ({
        ...promotion,
        deal: this.getDealFromPromotion(promotion),
      }));
    }

    return promotions;
  }

  removePromosWithRedeemedDeals(
    promotions: Array<MoblicoPromotionType>,
  ): Array<MoblicoPromotionType> {
    if (promotions) {
      return promotions.filter(
        (promotion) =>
          (promotion.deal && this.isDealActive(promotion.deal)) ||
          !promotion.deal,
      );
    }

    return promotions;
  }

  /** function to sort dates. */
  private getSortDate(item: Deal) {
    return moment(item.endDate).valueOf();
  };

  getActiveDeals() {
    const deals = DealRepository.getDeals();
    let activeDeals = deals.filter((deal) => this.isDealActive(deal));

    activeDeals = activeDeals.sort((a: Deal, b: Deal) => {
      const bDate = this.getSortDate(b);
      const aDate = this.getSortDate(a);
      return aDate - bDate;
    });

    // Add some demo offers to show the share experience
    // if (
    //   AccountStore.getOrgName()?.toUpperCase().includes('TEST') ||
    //   AccountStore.getLocationName()?.toUpperCase().includes('TEST') ||
    //   AccountStore.getOrgName()?.toUpperCase().includes('SUPPORT') ||
    //   AccountStore.getLocationName()?.toUpperCase().includes('SUPPORT')
    // ) {
    //   activeDeals.push(
    //     {
    //       currentGoalAmount: 0,
    //       currentGoalQuantity: 0,
    //       dealStatus: 8,
    //       discountAmount: 0.5,
    //       description: '$0.50 OFF Gushers',
    //       endDate: 1750481700000,
    //       externalServiceId: 4,
    //       image:
    //         'https://content.syndigo.com/asset/a9768b46-b9ae-427d-8482-9b25137dbc55/960.jpg?odnHeight=117&odnWidth=117&odnBg=FFFFFF',
    //       isRead: undefined,
    //       legalese:
    //         '$0.50 off Gushers. Availability may vary by location. Limit 5 redemptions per user. While supplies last.',
    //       message: undefined,
    //       name: '$0.50 OFF Gushers',
    //       numberOfUsesPerCode: 0,
    //       promotionText: '$0.50 OFF Gushers',
    //       redeemedCount: 0,
    //       targetGoalAmount: 0,
    //       targetGoalQuantity: 0,
    //       isShareable: true,
    //     } as Deal,
    //     {
    //       currentGoalAmount: 0,
    //       currentGoalQuantity: 0,
    //       dealStatus: 8,
    //       discountAmount: 1.5,
    //       description: 'Buy One, Get One 50% Off Red Bull',
    //       endDate: 1750481700000,
    //       externalServiceId: 4,
    //       image:
    //         'https://www.drinksupermarket.com/media/wysiwyg/Homepage/5_Brands/redbull-banner-sept-2023.jpg',
    //       isRead: undefined,
    //       legalese:
    //         'Availability may vary by location. Limit 5 redemptions per user. While supplies last.',
    //       message: undefined,
    //       name: 'Buy One, Get One 50% Off Red Bull',
    //       numberOfUsesPerCode: 0,
    //       promotionText: 'Buy One, Get One 50% Off Red Bull',
    //       redeemedCount: 0,
    //       targetGoalAmount: 0,
    //       targetGoalQuantity: 0,
    //       isShareable: true,
    //     } as Deal,
    //   );
    // }

    return activeDeals;
  }

  private isDealActive(deal: Deal): boolean {
    if (deal) {
      const now = moment();
      return (
        now.isBefore(deal.endDate) &&
        (deal.numberOfUsesPerCode === 0 ||
          deal.redeemedCount < deal.numberOfUsesPerCode)
      );
    }

    return false;
  }

  private getDealFromPromotion(promotion: MoblicoPromotionType) {
    if (
      promotion &&
      promotion.clickToUrl &&
      promotion.clickToUrl.includes('getDeals;')
    ) {
      const parts = promotion.clickToUrl.split(',');

      if (parts.length > 0) {
        const id = parts[parts.length - 1];
        const deal = DealRepository.getDeal(id);
        return deal;
      }
    }

    return null;
  }
}

export default new DealService();
